import { z } from 'zod';

const languageSchema = z.object({
  language: z.string(),
  fluency: z.array(z.enum(['Spoken', 'Written'])),
});

const singleShiftSchema = z.object({
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  workingHours: z.number().optional(),
});

const dayShiftSchema = z.array(singleShiftSchema).optional();

const paymentRecordSchema = z.object({
  toAgent: z.object({
    currency: z.enum(['USD', 'EUR', 'GBP', 'INR']).optional(),
    amount: z.number().optional(),
    payType: z.enum(['hourly', 'fixed', 'ticket_based']).optional(),
  }),
  fromClient: z.object({
    currency: z.enum(['USD', 'EUR', 'GBP', 'INR']).optional(),
    amount: z.number().optional(),
    payType: z.enum(['hourly', 'fixed', 'ticket_based']).optional(),
  }),
  effectiveStartDate: z.string().optional(),
  effectiveEndDate: z.string().optional(),
});

export const agentBrandSchema = z.object({
  brandId: z.string(),
  agentId: z.string(),
  email: z.string().email('Invalid email address'),
  password: z.string().optional(),
  payment: z.array(paymentRecordSchema).min(1, 'At least one payment record is required').optional(),
  onboardingDate: z.string().optional(),
  bonus: z
    .object({
      name: z.string().optional(),
      amount: z.number().optional(),
      frequency: z
        .enum(['monthly', 'weekly', 'daily', 'fortnightly', 'one-time', 'specific_day_of_week', 'specific_day_of_month'])
        .optional(),
      dayOfWeek: z.number().optional(),
      dayOfMonth: z.number().optional(),
    })
    .optional(),
  workSchedule: z
    .object({
      timeSlots: z
        .object({
          Mon: dayShiftSchema,
          Tue: dayShiftSchema,
          Wed: dayShiftSchema,
          Thu: dayShiftSchema,
          Fri: dayShiftSchema,
          Sat: dayShiftSchema,
          Sun: dayShiftSchema,
        })
        .partial(),
      breakDuration: z
        .object({
          value: z.number().optional(),
          unit: z.enum(['minutes', 'hours']).optional(),
        })
        .optional(),
    })
    .optional(),
  trialPeriod: z
    .object({
      days: z.number().optional(),
      startDate: z.string().optional(),
      completed: z.boolean().optional(),
    })
    .optional(),
  timezone: z.string().optional(),
  captureTimeInterval: z.number().optional(),
  tolerancePeriod: z.number().optional(),
  upworkContractId: z.string().optional(),
  upworkFreelancerId: z.string().optional(),
  isUpwork: z.boolean(),
  overdueSessionThresholdMinutes: z.number().optional(),
  idleTimeThresholdMinutes: z.number().optional(),
  monthlyExpectedWorkHours: z.number().min(1).optional(),
  enableLeaveFeature: z.boolean().optional(),
  slackAlertChannels: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  if (data.isUpwork) {
    if (!data.upworkContractId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Contract ID is required for Upwork', path: ['upworkContractId'] });
    }
    if (!data.upworkFreelancerId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Freelancer ID is required for Upwork', path: ['upworkFreelancerId'] });
    }
  }
});

export const agentSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  dob: z.string().optional().nullable(),
  personalEmail: z
    .string()
    .min(1, 'Personal email is required')
    .email('Invalid email address'),
  phoneNumber: z.object({
    countryCode: z.string(),
    number: z.string(),
    countryCodeEnum: z.string(),
  }),
  whatsappNumber: z
    .object({
      countryCode: z.string(),
      number: z.string(),
      countryCodeEnum: z.string(),
    })
    .optional(),
  profileUri: z.string().optional(),
  resumeUri: z.string().optional(),
  applicationVideoUri: z.string().optional(),
  governmentId: z.string().optional(),
  emergencyContact: z
    .object({
      name: z.string(),
      phoneNumber: z.string(),
    })
    .optional(),
  currentAddress: z.string().optional(),
  permanentAddress: z.string().optional(),
  pastEmployers: z
    .array(
      z.object({
        employerName: z.string(),
        employerUrl: z.string(),
        startDate: z.string(),
        endDate: z.string(),
      })
    )
    .optional(),
  totalExperience: z.string().or(z.number()).optional(),
  hobbies: z.array(z.string()).optional(),
  upworkProfile: z.string().optional(),
  status: z
    .enum(['active', 'inactive', 'on_leave', 'terminated'])
    .default('active'),
  skills: z.array(z.string()).optional(),
  languages: z.array(languageSchema).optional(),
  tools: z.array(z.string()).optional(),
  channels: z.array(z.string()).optional(),
  bankDetails: z
    .object({
      bankName: z.string().optional(),
      name: z.string().optional(),
      accountNumber: z.string().optional(),
      accountType: z.string().optional(),
      bankCode: z.string().optional(),
      branchName: z.string().optional(),
      city: z.string().optional(),
      bankStatement: z.string().optional(),
    })
    .optional(),
  paymentIds: z
    .object({
      paypalId: z.string().optional(),
      payoneerId: z.string().optional(),
      wiseId: z.string().optional(),
    })
    .optional(),
  preferredPaymentMethod: z
    .enum(['paypal', 'payoneer', 'wise', 'bank'])
    .optional(),
  monthlyExpectedWorkHours: z
    .number({ invalid_type_error: 'Must be a number' })
    .min(1, 'Must be at least 1 hour')
    .optional(),
  industry: z.string().optional(),
  relevantIndustries: z.array(z.string()).optional(),
  comments: z.string().optional(),
  agentBrands: z.array(agentBrandSchema).optional(),
}).superRefine((data, ctx) => {
  if (!data.bankDetails?.city) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'City is required.',
      path: ['bankDetails', 'city'],
    });
  }

  const method = data.preferredPaymentMethod;
  if (!method) return;

  if (method === 'bank') {
    const required: { key: keyof NonNullable<typeof data.bankDetails>; label: string }[] = [
      { key: 'name', label: 'Account holder name' },
      { key: 'bankName', label: 'Bank name' },
      { key: 'accountNumber', label: 'Account number' },
      { key: 'bankCode', label: 'Bank code' },
      { key: 'city', label: 'City' },
    ];
    for (const { key, label } of required) {
      if (!data.bankDetails?.[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required.`,
          path: ['bankDetails', key],
        });
      }
    }
  } else {
    const idField = `${method}Id` as keyof NonNullable<typeof data.paymentIds>;
    if (!data.paymentIds?.[idField]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${method.charAt(0).toUpperCase() + method.slice(1)} ID is required when selected as preferred payment method`,
        path: ['paymentIds', idField],
      });
    }
  }
});

export type FormInputs = z.infer<typeof agentSchema>;
